import React, { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button, type ButtonVariant } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  confirmVariant?: ButtonVariant;
  justificationLabel?: string;
  justificationRequired?: boolean;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (justification: string) => void;
}

export function PurchaseActionDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmVariant = 'primary',
  justificationLabel = 'Justificativa',
  justificationRequired = false,
  loading,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const titleId = useId();
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (!open) setJustification('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const canConfirm = !justificationRequired || justification.trim().length >= 5;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <header className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
          </div>
          <button type="button" aria-label="Fechar" onClick={onCancel} className="text-gray-400 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="space-y-4 px-5 py-4">
          {error && <Alert variant="error">{error}</Alert>}
          <Textarea
            label={`${justificationLabel}${justificationRequired ? ' *' : ''}`}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            minLength={justificationRequired ? 5 : undefined}
            showCharCount
          />
        </div>
        <footer className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
          <Button variant={confirmVariant} loading={loading} disabled={!canConfirm} onClick={() => onConfirm(justification.trim())}>
            {confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}
